import { dealsApi, deactivateDeal } from "@/api/deals-api";
import { getErrorMessage } from "@/lib/api-error";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  BusinessModels,
  GICSIndustries,
  GICSSubIndustries,
  cn,
} from "@/lib/utils";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import { useCurrentDealStore } from "@/store/current-deal-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useUser } from "@clerk/clerk-react";
import {
  ChevronDown,
  ChevronRight,
  Gem,
  Globe,
  Loader2,
  MessageCircle,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { CompanyAvatar } from "@/components/deals/company-avatar";
import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router";

interface DealHeaderV2Props {
  currentDeal: DealTransformed;
  isProcessing?: boolean;
  onStatusChange?: (status: string) => void;
  onDelete?: () => void;
  onInviteFounder?: () => void;
  invitationStatus?: { hasInvitation: boolean } | null;
  /**
   * Toggles the right-side AI chat panel. When undefined, the Ask AI
   * affordance is not rendered (e.g. legacy callers that do not own a
   * right panel).
   */
  onToggleAiChat?: () => void;
  /**
   * Whether the AI chat is currently open. Drives the Ask AI button's
   * active state so it lights up only while chat is visible, not while
   * an unrelated report is open in the same panel.
   */
  isChatActive?: boolean;
}

export function DealHeaderV2({
  currentDeal,
  isProcessing,
  onStatusChange,
  onDelete,
  onInviteFounder,
  invitationStatus,
  onToggleAiChat,
  isChatActive = false,
}: DealHeaderV2Props) {
  const { activeWorkspace, activeWorkspaceId } = useWorkspaceStore();
  const updateDeal = useCurrentDealStore((s) => s.updateDeal);
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const handleDeactivate = async () => {
    if (!activeWorkspaceId) return;
    setIsDeactivating(true);
    try {
      await deactivateDeal(activeWorkspaceId, currentDeal.id);
      updateDeal({ aiTracked: false });
      queryClient.invalidateQueries({ queryKey: ["orgQuota"] });
      toast.success("Deal marked as not premium");
      setDeactivateOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update deal"));
    } finally {
      setIsDeactivating(false);
    }
  };

  const statuses = activeWorkspace?.statuses || [];
  const currentStatus = currentDeal.overview.status;
  const currentIndex = statuses.findIndex((s) => s.title === currentStatus);
  const nextStatus =
    currentIndex >= 0 && currentIndex < statuses.length - 1
      ? statuses[currentIndex + 1]
      : null;

  const handleStatusChange = async (status: string) => {
    try {
      await dealsApi.changeStatus(
        currentDeal.id,
        status,
        user?.id ?? "",
        activeWorkspaceId ?? ""
      );
      updateDeal({
        overview: { ...currentDeal.overview, status },
      });
      onStatusChange?.(status);
      toast.success(`Moved to ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  // Prefer dealInfo.deal (source of truth — reflects pending edits) over
  // the cached overview snapshot.
  const dealField = (apiName: string): string | null => {
    const f = currentDeal.dealInfo.deal?.find((d) => d.field_name === apiName);
    const v = f?.field_value;
    if (typeof v === "string" && v.trim()) return v;
    if (Array.isArray(v) && v.length > 0)
      return v.filter(Boolean).join(", ");
    return null;
  };

  const resolveIndustry = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (!/^\d+$/.test(trimmed)) return trimmed;
    return (
      GICSSubIndustries.find((si) => si.code === trimmed)?.name ||
      GICSIndustries.find((i) => i.code === trimmed)?.name ||
      null
    );
  };

  const pickFirst = (raw: string | null): string | null => {
    if (!raw) return null;
    const first = raw.split(",")[0]?.trim();
    return first || null;
  };

  const industryLabel = pickFirst(dealField("industries"));
  const subIndustryLabel = pickFirst(dealField("sub_industries"));
  const resolvedIndustry = [industryLabel, subIndustryLabel]
    .map((v) => (v ? resolveIndustry(v) : null))
    .filter(Boolean)
    .join(" / ");

  const industrySegment =
    resolvedIndustry ||
    (currentDeal.overview.industry?.filter(Boolean).join(" / ") ?? "");

  const businessModelRaw =
    dealField("business_model") || currentDeal.overview.businessModel || "";
  const businessModelSegment = businessModelRaw
    ? businessModelRaw
        .split(",")
        .map((v) => {
          const t = v.trim();
          return BusinessModels.find((m) => m.value === t)?.label || t;
        })
        .filter(Boolean)
        .join(", ")
    : "";

  const meta = [industrySegment, businessModelSegment, currentDeal.overview.source].filter(
    Boolean
  );

  // Shared action-menu items used by both the mobile overflow menu and the
  // desktop overflow menu. Defined once so the menus stay in sync.
  const renderOverflowMenuItems = () => (
    <>
      <DropdownMenuItem asChild>
        <Link
          to={`/${activeWorkspaceId}/deals/${currentDeal.id}?tab=profile&edit=1`}
        >
          Edit deal
        </Link>
      </DropdownMenuItem>
      {onInviteFounder && (
        <DropdownMenuItem
          onClick={onInviteFounder}
          className="flex items-center justify-between gap-2"
        >
          <span>
            {invitationStatus?.hasInvitation
              ? "Manage invitations"
              : "Invite founder"}
          </span>
          {invitationStatus?.hasInvitation && (
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          )}
        </DropdownMenuItem>
      )}
      {currentDeal.aiTracked && (
        <DropdownMenuItem onClick={() => setDeactivateOpen(true)}>
          Mark as not premium
        </DropdownMenuItem>
      )}
      {onDelete && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete} className="text-destructive">
            Delete deal
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  return (
    <div className="border-b border-border bg-card">
      {/* Mobile compact layout (<sm) — collapses the desktop header to ~2 rows
          so tabs are visible without scrolling. Metadata moves into a
          disclosure; the Move Stage submenu folds into a single overflow
          alongside Edit/Invite/Delete to keep the action row a single line. */}
      <div className="sm:hidden flex flex-col gap-2 px-4 py-3">
        {/* Row 1: avatar + name + stage chip */}
        <div className="flex items-center gap-2 min-w-0">
          <CompanyAvatar
            companyFiles={currentDeal.company_files}
            websiteUrl={currentDeal.overview.website}
            alt={currentDeal.overview.name}
            className="h-8 w-8 shrink-0 bg-background ring-1 ring-border"
            iconClassName="h-4 w-4"
          />
          <h1 className="flex-1 min-w-0 truncate text-lg font-medium text-foreground">
            {currentDeal.overview.name}
          </h1>
          {isProcessing ? (
            <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground/80">
              <Loader2 className="h-3 w-3 animate-spin" />
              Enriching
            </span>
          ) : (
            <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-foreground/40" />
              {currentStatus}
            </span>
          )}
          {currentDeal.aiTracked && (
            <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
              <Gem className="h-3 w-3" />
              Premium
            </span>
          )}
        </div>

        {/* Row 2: Ask AI + overflow menu (Move stage submenu lives inside) */}
        {!isProcessing && (
          <div className="flex items-center gap-1.5">
            {onToggleAiChat && (
              <button
                onClick={onToggleAiChat}
                className={cn(
                  "flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs transition-colors",
                  isChatActive
                    ? "bg-accent font-medium text-accent-foreground"
                    : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
                )}
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Ask AI
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-auto flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground">
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {statuses.length > 0 && (
                  <>
                    {statuses.map((s) => {
                      const itemDotStyle = s.colour?.startsWith("#")
                        ? { backgroundColor: s.colour }
                        : undefined;
                      return (
                        <DropdownMenuItem
                          key={s.title}
                          onClick={() => handleStatusChange(s.title)}
                          className={cn(
                            s.title === currentStatus && "font-medium"
                          )}
                        >
                          <span
                            aria-hidden
                            className={
                              itemDotStyle
                                ? "mr-2 h-1.5 w-1.5 shrink-0 rounded-full"
                                : "mr-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/40"
                            }
                            style={itemDotStyle}
                          />
                          Move to {s.title}
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuSeparator />
                  </>
                )}
                {renderOverflowMenuItems()}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Row 3: collapsible metadata details */}
        {(meta.length > 0 || currentDeal.overview.website) && (
          <Collapsible
            open={mobileDetailsOpen}
            onOpenChange={setMobileDetailsOpen}
          >
            <CollapsibleTrigger className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 transition-colors hover:text-foreground">
              <ChevronDown
                className={cn(
                  "h-3 w-3 transition-transform",
                  mobileDetailsOpen && "rotate-180"
                )}
              />
              Details
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-1.5 flex flex-col gap-1 text-xs text-muted-foreground">
              {meta.map((item, i) => (
                <span key={i}>{item}</span>
              ))}
              {currentDeal.overview.website && (
                <a
                  href={
                    currentDeal.overview.website.startsWith("http")
                      ? currentDeal.overview.website
                      : `https://${currentDeal.overview.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
                >
                  <Globe className="h-3 w-3 shrink-0" />
                  <span className="min-w-0 truncate">
                    {currentDeal.overview.website.replace(/^https?:\/\//, "")}
                  </span>
                </a>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>

      {/* Desktop layout (sm+) — unchanged from previous behaviour. */}
      <div className="hidden sm:flex items-start justify-between gap-6 px-6 py-4">
        {/* Left */}
        <div className="min-w-0 flex-1">
          {/* Stage badge */}
          <div className="flex items-center gap-1.5">
            {isProcessing ? (
              <>
                <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground/50" />
                <span className="text-xs font-medium text-muted-foreground/50">
                  Enriching
                </span>
              </>
            ) : (
              <>
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/40" />
                <span className="text-xs font-medium text-muted-foreground">
                  {currentStatus}
                </span>
              </>
            )}
            {currentDeal.aiTracked && (
              <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                <Gem className="h-3 w-3" />
                Premium
              </span>
            )}
          </div>

          {/* Company name */}
          <h1 className="mt-1 text-3xl font-medium text-foreground sm:text-4xl">
            {currentDeal.overview.name}
          </h1>

          {/* Metadata */}
          {meta.length > 0 && (
            <div className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-1.5">
              {meta.map((item, i) => (
                <span key={i} className="inline-flex items-center gap-1.5">
                  {i > 0 && (
                    <span className="hidden text-muted-foreground/30 sm:inline">
                      &middot;
                    </span>
                  )}
                  {item}
                </span>
              ))}
              {currentDeal.overview.website && (
                <>
                  <span className="hidden text-muted-foreground/30 sm:inline">
                    &middot;
                  </span>
                  <a
                    href={
                      currentDeal.overview.website.startsWith("http")
                        ? currentDeal.overview.website
                        : `https://${currentDeal.overview.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
                  >
                    <Globe className="h-3 w-3" />
                    {currentDeal.overview.website.replace(/^https?:\/\//, "")}
                  </a>
                </>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        {!isProcessing && (
          <div className="flex shrink-0 items-center gap-1.5 pt-1">
            {/* Ask AI */}
            {onToggleAiChat && (
              <button
                onClick={onToggleAiChat}
                className={cn(
                  "flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs transition-colors",
                  isChatActive
                    ? "bg-accent font-medium text-accent-foreground"
                    : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
                )}
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Ask AI
              </button>
            )}

            {/* Move Stage */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground">
                  {nextStatus ? (
                    <>
                      Move to {nextStatus.title}
                      <ChevronRight className="h-3 w-3 opacity-40" />
                    </>
                  ) : (
                    "Move Stage"
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {statuses.map((s) => {
                  const itemDotStyle = s.colour?.startsWith("#")
                    ? { backgroundColor: s.colour }
                    : undefined;
                  return (
                    <DropdownMenuItem
                      key={s.title}
                      onClick={() => handleStatusChange(s.title)}
                      className={cn(
                        s.title === currentStatus && "font-medium"
                      )}
                    >
                      <span
                        aria-hidden
                        className={
                          itemDotStyle
                            ? "mr-2 h-1.5 w-1.5 shrink-0 rounded-full"
                            : "mr-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/40"
                        }
                        style={itemDotStyle}
                      />
                      {s.title}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* More */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground">
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {renderOverflowMenuItems()}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <AlertDialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this deal as not premium?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium">{currentDeal.overview.name}</span>{" "}
              will stop counting toward your plan and its premium reports will be
              locked until you mark it premium again. No report data is deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeactivating}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeactivate();
              }}
              disabled={isDeactivating}
            >
              {isDeactivating ? "Marking…" : "Mark as not premium"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
